import React, { useEffect, useState } from 'react';
import { getSites, getCoreStatus, updateCore } from '../api';
import { RefreshCw, Shield, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

export default function CoreUpdates() {
  const [sites, setSites] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [updating, setUpdating] = useState({});
  const [log, setLog] = useState([]);

  useEffect(() => {
    getSites().then(s => {
      setSites(s);
      s.forEach(site => getCoreStatus(site.id).then(st => setStatuses(p=>({...p,[site.id]:st}))).catch(()=>{}));
    });
  }, []);

  const addLog = (msg, cls='info') => setLog(p=>[...p,{msg,cls}]);

  const handleUpdate = async site => {
    setUpdating(u=>({...u,[site.id]:true})); setLog([]);
    addLog(`→ Safe update: ${site.name}...`);
    try {
      const res = await updateCore(site.id);
      res.steps.forEach(s => {
        if (s.step==='backup') addLog(s.success?'✓ Backup done':`⚠ Backup: ${s.note}`, s.success?'ok':'warn');
        if (s.step==='update') addLog(s.success?`✓ WP: ${s.version_before} → ${s.version_after}`:'✗ Update failed', s.success?'ok':'err');
        if (s.step==='verify') addLog(s.success?`✓ Site online (${s.response_ms}ms)`:'✗ Verify failed', s.success?'ok':'err');
      });
      if (res.success) {
        toast.success(`${site.name} updated!`);
        setStatuses(p=>({...p,[site.id]:{...p[site.id],needs_update:false}}));
      }
    } catch(err) { addLog(`✗ ${err?.error||err}`,'err'); toast.error('Update failed'); }
    finally { setUpdating(u=>({...u,[site.id]:false})); }
  };

  return (
    <>
      <div className="page-header">
        <div><h1>WordPress Core Updates</h1><p>Safe update: backup → update → verify</p></div>
      </div>
      <div className="page-content">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div className="card">
            <div className="card-head"><h3><RefreshCw size={14} /> Core versions</h3></div>
            <table className="tbl">
              <thead><tr><th>Site</th><th>Current</th><th>Latest</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {!sites.length && <tr><td colSpan={5} style={{ textAlign:'center', padding:24, color:'var(--text3)' }}>No sites</td></tr>}
                {sites.map(s => {
                  const st = statuses[s.id];
                  return (
                    <tr key={s.id}>
                      <td className="bold">{s.name}</td>
                      <td>{st?.current_version||s.wp_version||'—'}</td>
                      <td style={{ color:'var(--green-text)' }}>{st?.latest_version||'—'}</td>
                      <td>{!st?<span className="badge gray">Checking</span>:st.needs_update?<span className="badge amber">Update available</span>:<span className="badge green">Up to date</span>}</td>
                      <td>{st?.needs_update?<Button className="btn sm amber" onClick={()=>handleUpdate(s)} loading={updating[s.id]}><Shield size={11}/> {updating[s.id]?'...':'Safe update'}</Button>:<span style={{fontSize:11,color:'var(--text3)'}}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card">
            <div className="card-head"><h3><Shield size={14} /> Update log</h3></div>
            <div style={{ padding:'12px 16px' }}>
              <div style={{ background:'var(--amber-bg)', border:'1px solid transparent', borderRadius:10, padding:'10px 12px', fontSize:11, color:'var(--amber-text)', display:'flex', gap:8, marginBottom:12 }}>
                <AlertTriangle size={14} style={{ flexShrink:0 }} />
                Backup → Update WP core → Verify site online. Backup requires UpdraftPlus.
              </div>
              <div className="log-box">
                {!log.length&&<span className="info">// Click "Safe update" to begin</span>}
                {log.map((l,i)=><div key={i} className={l.cls}>{l.msg}</div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
